import { useState } from "react"
import { DateRange } from "react-day-picker"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { startOfDay } from "date-fns"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DateRangeInput } from "@/components/ui/date-range-input"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { BirthDatePicker } from "@/modules/common/address-section/birth-date-picker"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import {
  DeLegalGuardianAttestationSchema,
  DeLegalGuardianAttestationSchemaType,
} from "@/modules/patients/detail/legal-guardian-attestation/de/schema/de-legal-guardian-attestation-schema"
import { deLegalGuardianAttestationFragment } from "@/modules/patients/detail/legal-guardian-attestation/de/services/deLegalGuardianAttestationFragment"
import { useDeCreateLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeCreateLegalGuardianAttestationMutation"
import { useDeDeleteLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeDeleteLegalGuardianAttestationMutation"
import { useDeGenerateLegalGuardianAttestationDocumentMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeGenerateLegalGuardianAttestationDocumentMutation"
import { useDeUpdateLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/de/services/useDeUpdateLegalGuardianAttestationMutation"
import { useLegalGuardianAttestationDrawerContext } from "@/modules/patients/detail/legal-guardian-attestation/store/useLegalGuardianAttestationDrawerStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

const today = startOfDay(new Date())

const DeLegalGuardianAttestationDrawer = withErrorBoundary(() => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [createLegalGuardianAttestation, isCreating] = useDeCreateLegalGuardianAttestationMutation()
  const [updateLegalGuardianAttestation, isUpdating] = useDeUpdateLegalGuardianAttestationMutation()
  const [deleteLegalGuardianAttestation] = useDeDeleteLegalGuardianAttestationMutation()
  const [generateDeLegalGuardianDocument, isGenerating] = useDeGenerateLegalGuardianAttestationDocumentMutation()

  const { toast } = useToast()

  const progressNoteId = useLegalGuardianAttestationDrawerContext((state) => state.progressNoteId)
  const legalGuardianAttestationId = useLegalGuardianAttestationDrawerContext(
    (state) => state.legalGuardianAttestationId,
  )
  const legalGuardianAttestationRef = useLegalGuardianAttestationDrawerContext(
    (state) => state.legalGuardianAttestationRef,
  )
  const setLegalGuardianAttestation = useLegalGuardianAttestationDrawerContext(
    (state) => state.setLegalGuardianAttestation,
  )

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const isEditMode = !!legalGuardianAttestationId
  const isAddMode = !isEditMode && patientId && progressNoteId
  const persistedData = useFragment(deLegalGuardianAttestationFragment, legalGuardianAttestationRef)
  const updatedAt = persistedData?.updatedAt || persistedData?.createdAt
  const updatedBy = persistedData?.updatedBy || persistedData?.createdBy

  const parsedData = isEditMode
    ? DeLegalGuardianAttestationSchema.parse({
        ...persistedData,
        durationFrom: new Date(persistedData?.durationFrom ?? ""), // default will result in an invalid date or the minimum date in 1970...
        durationTo: new Date(persistedData?.durationTo ?? ""), // default will result in an invalid date or the minimum date in 1970...
        birthDate: new Date(persistedData?.birthDate ?? ""), // default will result in an invalid date or the minimum date in 1970...
      })
    : null

  const form = useForm<DeLegalGuardianAttestationSchemaType>({
    resolver: zodResolver(DeLegalGuardianAttestationSchema),
    defaultValues:
      isEditMode && parsedData
        ? {
            ...parsedData,
          }
        : {
            certificate: "FIRST",
            durationFrom: today,
            durationTo: undefined,
            firstName: "",
            lastName: "",
            birthDate: undefined,
          },
  })

  const {
    control,
    formState: { errors, isDirty },
    register,
    setValue,
    resetField,
    getValues,
  } = form

  useDirtyDrawerMaker(isDirty)

  const handleChangeDuration = (range?: DateRange) => {
    if (range?.from) {
      setValue("durationFrom", range.from, { shouldValidate: true, shouldDirty: true })
    }
  }

  const handleDurationInputChange = (_e: React.ChangeEvent<HTMLInputElement>, range?: DateRange) => {
    if (range?.from) setValue("durationFrom", range.from, { shouldValidate: true, shouldDirty: true })
    if (range?.to) setValue("durationTo", range.to, { shouldValidate: true, shouldDirty: true })
  }

  const handleCloseDrawer = () => {
    setLegalGuardianAttestation(null, null)
    closeDrawer()
  }

  const handleGenerateDocument = (id: string) => {
    if (id && progressNoteId) {
      generateDeLegalGuardianDocument({
        params: {
          id,
        },
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }
  }

  const onSubmit: SubmitHandler<DeLegalGuardianAttestationSchemaType> = (data) => {
    if (!progressNoteId) return

    const { durationFrom, durationTo, birthDate } = data

    const formattedData = {
      ...data,
      durationFrom: new DateOnly(durationFrom).toString(),
      durationTo: new DateOnly(durationTo).toString(),
      birthDate: new DateOnly(birthDate).toString(),
    }

    if (isAddMode) {
      createLegalGuardianAttestation({
        variables: {
          input: {
            patientId,
            progressNoteId,
            birthDate: formattedData.birthDate,
            certificate: formattedData.certificate,
            durationFrom: formattedData.durationFrom,
            durationTo: formattedData.durationTo,
            firstName: formattedData.firstName,
            lastName: formattedData.lastName,
          },
        },
        onCompleted(response) {
          const id = response.de_createAttestationGuardian.de_AttestationGuardian?.id
          if (id) {
            handleGenerateDocument(id)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const attestationGuardian = data?.de_createAttestationGuardian?.de_AttestationGuardian

            if (attestationGuardian?.id) {
              const newRecordProxy = store.get(attestationGuardian.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_attestationGuardian")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }

    if (isEditMode) {
      updateLegalGuardianAttestation({
        variables: {
          input: {
            id: legalGuardianAttestationId,
            birthDate: formattedData.birthDate,
            certificate: formattedData.certificate,
            durationFrom: formattedData.durationFrom,
            durationTo: formattedData.durationTo,
            firstName: formattedData.firstName,
            lastName: formattedData.lastName,
          },
        },
        onCompleted(response) {
          const id = response.de_updateAttestationGuardian.de_AttestationGuardian?.id
          if (id) {
            handleGenerateDocument(id)
          }
        },
      })
    }
  }

  const handleDelete = () => {
    if (progressNoteId && legalGuardianAttestationId) {
      deleteLegalGuardianAttestation({
        variables: {
          input: {
            id: legalGuardianAttestationId,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          handleOpenConfirmDialog(false)
          toast({
            description: t("patient.legalGuardianAttestation.deleteMessage.success"),
          })
        },
      })
    }
  }

  const handleOpenConfirmDialog = (isOpen: boolean) => {
    setIsOpenConfirmDialog(isOpen)
  }

  return (
    <>
      <Drawer open onOpenChange={handleCloseDrawer}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 pr-4" data-testid="legal-guardian-attestation-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("patient.legalGuardianAttestation.attestationForLegalGuardian")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="legal-guardian-attestation-body">
            <FormField
              control={control}
              name="durationTo"
              render={({ field: { value, onChange } }) => {
                return (
                  <DateRangeInput
                    data-testid="duration-input"
                    value={{ from: getValues("durationFrom"), to: value }}
                    onSelectDays={(range) => {
                      handleChangeDuration(range)
                      onChange(range?.to ?? null)
                    }}
                    onInputChange={handleDurationInputChange}
                    label={t("patient.legalGuardianAttestation.duration")}
                    error={errors.durationFrom?.message ?? errors.durationTo?.message}
                    translateError
                    required
                  />
                )
              }}
            />
            <div className="mb-4 grid gap-2">
              <p className="text-on-surface-subdued text-sm">{t("patient.sickLeaveCertificate.typeOfCertificate")}</p>
              <FormField
                control={control}
                name="certificate"
                render={({ field: { value, onChange } }) => (
                  <RadioGroup
                    className="flex flex-wrap"
                    data-testid="certificate-radio"
                    value={value}
                    onValueChange={(value) => onChange(value)}>
                    <RadioGroupItem value="FIRST" data-testid="first-certificate-radio-item">
                      <RadioGroupLabel>{t("patient.legalGuardianAttestation.firstCertificate")}</RadioGroupLabel>
                    </RadioGroupItem>
                    <RadioGroupItem value="FOLLOW_UP" data-testid="follow-up-certificate-radio-item">
                      <RadioGroupLabel>{t("patient.legalGuardianAttestation.followUpCertificate")}</RadioGroupLabel>
                    </RadioGroupItem>
                  </RadioGroup>
                )}
              />
            </div>
            <div className="grid gap-2">
              <p className="text-on-surface text-base font-medium tracking-tight">
                {t("patient.legalGuardianAttestation.legalGuardian")}
              </p>
              <div className="mt-2 grid gap-4">
                <div className="grid grid-cols-2 gap-5">
                  <Input
                    {...register("firstName")}
                    label={t("patient.legalGuardianAttestation.firstName")}
                    required
                    error={errors.firstName?.message}
                    translateError
                    errorClasses="truncate"
                    data-testid="first-name-input"
                  />
                  <Input
                    {...register("lastName")}
                    label={t("patient.legalGuardianAttestation.lastName")}
                    required
                    error={errors.lastName?.message}
                    translateError
                    errorClasses="truncate"
                    data-testid="last-name-input"
                  />
                </div>
                <Controller
                  control={control}
                  name="birthDate"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <BirthDatePicker
                      defaultValue={value}
                      error={error?.message}
                      onSelect={onChange}
                      onClear={() => resetField("birthDate")}
                    />
                  )}
                />
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="legal-guardian-attestation-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={() => handleOpenConfirmDialog(true)} data-testid="attestation-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                disabled={isEditMode && !isDirty}
                data-testid="attestation-save-button"
                isLoading={isCreating || isUpdating || isGenerating}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_legalGuardian_save")
                  form.handleSubmit(onSubmit)()
                }}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <DeprecatedConfirmationDialog
        title={t("patient.legalGuardianAttestation.deleteConfirmTitle")}
        content={t("episodeOfCare.deleteProgressNote.content")}
        open={isOpenConfirmDialog}
        onConfirm={handleDelete}
        confirmVariant="danger"
        confirmText={t("common.button.delete")}
        cancelText={t("common.button.keep")}
        onOpenChange={handleOpenConfirmDialog}
      />
    </>
  )
}, "DeLegalGuardianAttestationDrawer")

export { DeLegalGuardianAttestationDrawer }
